import { DataLayerService } from "../../../services/data-layer-service";
import { LocalizationService } from "../../../services/localization-service";
import { InputValidator } from "../../../validators/input-validator";
import { ValidationRule } from "./validation-rule.interface";

export class FieldController {
    private _localizationService: LocalizationService;
    private readonly _dataLayerService: DataLayerService;
    private input: HTMLInputElement;
    private errorContainer: HTMLElement;
    private rules: ValidationRule[];
    private watchers: ((value: string) => void)[] = [];
    private value: string = "";
    private debounceTimer: ReturnType<typeof setTimeout> | undefined;
    private debounceMs: number;
    private handleInput: () => void;
    private handleBlur: () => void;

    constructor(localizationService: LocalizationService, dataLayerService: DataLayerService, input: HTMLInputElement, errorContainer: HTMLElement, rules: ValidationRule[], debounceMs = 300) {
        this._localizationService = localizationService;
        this._dataLayerService = dataLayerService;
        this.input = input;
        this.errorContainer = errorContainer;
        this.rules = rules;
        this.debounceMs = debounceMs;

        this.handleInput = () => {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.value = this.input.value;
                this.watchers.forEach(fn => fn(this.value));
                this.validate();
            }, this.debounceMs);
        };

        this.handleBlur = () => this.validate();

        this.input.addEventListener("input", this.handleInput);
        this.input.addEventListener("blur", this.handleBlur);
    }

    public getValue(): string {
        return this.input.value;
    }

    public setValue(val: string): void {
        this.input.value = val;
        this.value = val;
        this.watchers.forEach(fn => fn(val));
        this.validate();
    }

    public onChange(fn: (value: string) => void): void {
        this.watchers.push(fn);
    }

    public async validate(triggerAsync = false, trackAnalytics = false): Promise<boolean> {
        const val = this.input.value;
        const errors: string[] = [];
        let ranValidation = false;

        for (const rule of this.rules) {
            if (rule.async === true && !triggerAsync) continue;

            ranValidation = true;
            const valid = await Promise.resolve(rule.validate(val));
            if (!valid) {
                errors.push(rule.message);
                if (trackAnalytics) {
                    InputValidator.trackValidationErrorByLtag(rule.message, this._localizationService, this._dataLayerService)
                }
            }
        }

        // Only clear/render errors if we actually ran validation
        if (ranValidation) {
            this.renderErrors(errors);
        }

        return errors.length === 0;
    }

    public focus(): void {
        this.input.focus();
    }

    public destroy(): void {
        this.input.removeEventListener("input", this.handleInput);
        this.input.removeEventListener("blur", this.handleBlur);
        this.input.classList.remove("invalid-input");
        this.errorContainer.innerHTML = "";
        clearTimeout(this.debounceTimer);
    }

    private renderErrors(ltags: string[]): void {
        this.input.classList.toggle("invalid-input", ltags.length > 0);
        this.errorContainer.innerHTML = "";
        if (ltags.length) {
            const ul = document.createElement("ul");
            ul.classList.add("error-list");
            for (const tag of ltags) {
                const li = document.createElement("li");
                li.setAttribute("ltag", tag);
                li.innerText = this._localizationService.names(tag);
                ul.appendChild(li);
            }
            this.errorContainer.appendChild(ul);
        }
    }
}
